import styles from "./MaterialActions.module.scss";
import { Action } from "./useMaterialActions";
import clsx from "clsx";
import MaterialAccessBlock from "./MaterialAccessBlock.tsx";
import { CourseMaterial } from "./coursesLoader.ts";
import useVirtualStore from "./useVirtualStore.tsx";
import StudentChoice from "./StudentChoice.tsx";
import { RefObject, useState } from "react";
import LoaderButton from "common/core-ui/LoaderButton.tsx";
import MaterialUnavailable from "./MaterialUnavailable.tsx";
import useMaterialOptInOutStatus from "./useMaterialOptInOutStatus.tsx";
import MaterialOptInOutStatus from "./MaterialOptInOutStatus.tsx";
import useStudentChoice from "./useStudentChoice.tsx";

const HIDDEN_ACTIONS = ["view_shipping", "update_shipping"];
const OUTLINED_CATEGORIES = ["illFavoredPurchase", "info"];

export function MaterialAction({
  action,
  className,
  isNaked,
  isOutlined,
}: {
  action: Action;
  className?: string;
  isNaked?: boolean;
  isOutlined?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const { label, icon, href, onClick, to } = action;
  const buttonStyle = isNaked
    ? "vst-button-variant-naked"
    : isOutlined || OUTLINED_CATEGORIES.includes(action.category)
    ? "vst-button-variant-outline"
    : "";

  return (
    <LoaderButton
      loading={loading}
      aria-label={action.ariaLabel}
      className={clsx("vst-button-pill", className, buttonStyle)}
      href={href}
      onClick={() => {
        if (onClick) {
          const result = onClick();
          if (result) {
            setLoading(true);
            void result.finally(() => setLoading(false));
          }
        }
      }}
      to={to}
    >
      <div className={clsx(styles.buttonInner)}>
        {icon}
        {label}
      </div>
    </LoaderButton>
  );
}

export default function MaterialActions({
  material,
  actions,
  className,
  pricingRef,
}: {
  material: CourseMaterial;
  actions: Action[];
  className?: string;
  pricingRef?: RefObject<HTMLDivElement>;
}) {
  const showActions = actions.filter(
    (action) => !HIDDEN_ACTIONS.includes(action.type ?? "")
  );
  const { isVirtualStore } = useVirtualStore();
  const { isOptInOutProcessing, optInOutStatus } =
    useMaterialOptInOutStatus(material);
  const optOutAction = showActions.find((action) => action.type === "opt_out");
  const optInAction = showActions.find((action) => action.type === "opt_in");
  const cartAction = showActions.find((action) => action.meta === "mini-pdp");
  const { isMinPDPActive, isMaterialOutOfStock } = useStudentChoice(material);
  if (isVirtualStore && !isMinPDPActive && cartAction) {
    // don't want action to count for purposes of naked "View Details"
    // as mini-PDP is not active
    showActions.splice(showActions.indexOf(cartAction), 1);
  }

  if (isVirtualStore && cartAction && !material.product) {
    return (
      <div className={clsx(className, styles.unavailable)}>
        <MaterialUnavailable />
      </div>
    );
  }

  if (
    isOptInOutProcessing &&
    optInOutStatus !== "none" &&
    optInOutStatus !== "opting_out"
  ) {
    // opt in and out and revalidation is slow. If the post could supply updated buttons
    // we could do optimistic UI but currently we can't, this is an interim solution
    // "opting_out" state covered by dialog
    return (
      <div className={className}>
        <MaterialOptInOutStatus material={material} />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* shows for virtual store only */}
      <MaterialAccessBlock
        material={material}
        onOptOut={optOutAction?.onClick}
        onOptIn={optInAction?.onClick}
      />
      {/* cart action gets expanded into a mini-pdp when appropriate */}
      <StudentChoice
        material={material}
        action={cartAction}
        pricingRef={pricingRef}
      />
      {/* in virtual store opt out is done via IAEA block, purchase via mini-pdp */}
      {showActions.map((action, i) => {
        return (isVirtualStore && action === optOutAction) ||
          (isVirtualStore && action === cartAction) ? null : (
          <MaterialAction
            key={action.key}
            action={action}
            className={styles.action}
            isNaked={
              action.category === "details" &&
              i === showActions.length - 1 &&
              showActions.length > 1
            }
            isOutlined={action.category === "details" && isMaterialOutOfStock}
          />
        );
      })}
    </div>
  );
}
